/**
 * HELP-CLIPBOARD.UTILS
 * Copy of a section of a guide to paste it in a mail, a document or a ticket : the HTML of the guide depends on the CSS of the
 * application (classes, SVG icons), so it is rewritten with inline styles and plain characters, which every editor keeps.
 * The images are sent with the text (as data) when the server allows it, otherwise they stay links.
 */

const TEXT_COLOR = "#111C2B"

//Icons of the guide (SVG) written as a colored character : a document editor does not keep SVG
const ICON_CHARACTERS: Record<string, string> = {
  circle: "●",
  "circle-check": "✓",
  "triangle-exclamation": "▲",
  "circle-info": "ⓘ",
  "arrow-down": "▼",
  "arrow-up": "▲",
  equals: "="
}

const TAG_STYLES: Record<string, string> = {
  "help-tag-risk": "background-color:#fde8e5;color:#b3392a",
  "help-tag-ok": "background-color:#dff5e8;color:#1d7a4a"
}

//Chips of the tags of the RPS questions (same colors as the questions page)
const CHIP_STYLES: Record<string, string> = {
  "help-chip-stress": "background-color:#ff9500;border:1px solid #ff9500;color:#ffffff",
  "help-chip-autonomie": "background-color:#8bbdd9;border:1px solid #8bbdd9;color:#ffffff"
}
const CHIP_DEFAULT_STYLE = "background-color:#ffffff;border:1px solid #8c8c8c;color:#8c8c8c"

const ELEMENT_STYLES: Record<string, string> = {
  h2: "font-size:18px;font-weight:bold;margin:16px 0 8px;",
  h3: "font-size:16px;font-weight:bold;margin:14px 0 6px;",
  p: "margin:0 0 12px;",
  ul: "margin:0 0 12px;padding-left:22px;",
  ol: "margin:0 0 12px;padding-left:22px;",
  figure: "margin:0 0 16px;",
  figcaption: "font-size:13px;color:#8C8C8C;margin-top:4px;",
  table: "border-collapse:collapse;margin:0 0 16px;font-size:14px;",
  th: "border:1px solid #e3e3e8;padding:8px 12px;text-align:left;vertical-align:top;background-color:#f4f4f8;font-weight:bold;",
  td: "border:1px solid #e3e3e8;padding:8px 12px;text-align:left;vertical-align:top;",
  blockquote: "margin:0 0 16px;padding:10px 14px;border-left:4px solid #5b54d6;background-color:#f0eff9;",
  img: "display:block;max-width:100%;height:auto;max-height:420px;border-radius:8px;margin:12px 0 6px;"
}

const BLOCK_TAGS = new Set(["p", "div", "h1", "h2", "h3", "h4", "ul", "ol", "li", "tr", "table", "figure", "figcaption", "blockquote", "hr"])

function parse(html: string): HTMLElement {
  const root = document.createElement("div")
  root.innerHTML = html
  return root
}

function colorOf(element: Element, property: "color" | "background-color"): string {
  return (element as HTMLElement).style?.getPropertyValue(property) || ""
}

function addStyle(element: HTMLElement, style: string) {
  element.setAttribute("style", style + (element.getAttribute("style") ?? ""))
}

//Same text, written to be pasted : inline styles, colored characters instead of SVG and CSS classes
export function buildHelpClipboardHtml(title: string, bodyHtml: string, baseUrl: string): string {
  const root = parse(bodyHtml)

  //Icons : the icon of a tab (decoration in front of a name) is dropped, the others become a colored character
  root.querySelectorAll("svg").forEach((svg) => {
    const name = svg.getAttribute("data-icon") ?? ""
    const character = ICON_CHARACTERS[name]
    if (svg.classList.contains("help-tab-icon") || !character) return svg.remove()
    const span = document.createElement("span")
    span.setAttribute("style", `color:${colorOf(svg, "color") || TEXT_COLOR};font-weight:bold;`)
    span.textContent = `${character} `
    svg.replaceWith(span)
  })

  //Colored dots, notes and tags
  root.querySelectorAll("span.help-dot").forEach((dot) => {
    const span = document.createElement("span")
    span.setAttribute("style", `color:${colorOf(dot, "background-color") || TEXT_COLOR};`)
    span.textContent = "● "
    dot.replaceWith(span)
  })
  root.querySelectorAll("span.help-pill").forEach((pill) => {
    addStyle(
      pill as HTMLElement,
      `display:inline-block;min-width:36px;padding:2px 10px;border-radius:999px;color:#ffffff;font-weight:bold;text-align:center;`
    )
    pill.removeAttribute("class")
  })
  root.querySelectorAll("span.help-tag").forEach((tag) => {
    const kind = Array.from(tag.classList).find((x) => TAG_STYLES[x])
    tag.setAttribute(
      "style",
      `display:inline-block;padding:1px 12px;border-radius:999px;font-weight:bold;${kind ? TAG_STYLES[kind] : ""}`
    )
    tag.removeAttribute("class")
  })

  root.querySelectorAll("span.help-chip").forEach((chip) => {
    const kind = Array.from(chip.classList).find((x) => CHIP_STYLES[x])
    chip.setAttribute(
      "style",
      `display:inline-block;padding:2px 14px;border-radius:16px;font-weight:bold;${kind ? CHIP_STYLES[kind] : CHIP_DEFAULT_STYLE}`
    )
    chip.removeAttribute("class")
  })

  //Links to another guide only mean something in the application : plain text
  root.querySelectorAll("a").forEach((link) => {
    if ((link.getAttribute("href") ?? "").startsWith("#guide-")) link.replaceWith(...Array.from(link.childNodes))
  })

  //Images : full address, so they still load once pasted
  root.querySelectorAll("img").forEach((image) => {
    const src = image.getAttribute("src") ?? ""
    try {
      image.setAttribute("src", new URL(src, baseUrl).href)
    } catch {
      /* the address is kept as it is */
    }
  })

  //Styles of the elements (the style an element already has, a colored cell for example, comes last and wins)
  Object.entries(ELEMENT_STYLES).forEach(([tag, style]) => {
    root.querySelectorAll(tag).forEach((element) => addStyle(element as HTMLElement, style))
  })
  root.querySelectorAll("figure.table").forEach((figure) => figure.setAttribute("style", "margin:0 0 16px;"))
  root.querySelectorAll("[class]").forEach((element) => element.removeAttribute("class")) //The classes mean nothing outside the application

  const heading = document.createElement("h2")
  heading.setAttribute("style", `font-size:20px;font-weight:bold;margin:0 0 12px;color:${TEXT_COLOR};`)
  heading.textContent = title

  const wrapper = document.createElement("div")
  wrapper.setAttribute(
    "style",
    `font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:${TEXT_COLOR};`
  )
  wrapper.append(heading, ...Array.from(root.childNodes))
  return wrapper.outerHTML
}

//Plain text of the same content, for the places that do not take HTML
export function htmlToPlainText(html: string): string {
  const root = parse(html)
  root.querySelectorAll("img").forEach((image) => {
    const alt = image.getAttribute("alt")
    image.replaceWith(document.createTextNode(alt ? `[Capture : ${alt}]\n` : ""))
  })
  root.querySelectorAll("li").forEach((item) => item.prepend(document.createTextNode("• ")))
  root.querySelectorAll("th, td").forEach((cell) => cell.append(document.createTextNode(" | ")))
  root.querySelectorAll("*").forEach((element) => {
    if (BLOCK_TAGS.has(element.tagName.toLowerCase())) element.append(document.createTextNode("\n"))
  })
  return (root.textContent ?? "")
    .replace(/ /g, " ")
    .replace(/ \| \n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

//Widest an image is pasted : a document editor ignores max-width and shows the real size of the image, which fills the page
const MAX_IMAGE_WIDTH = 560

//Real size of an image (the browser reads it even when the server does not allow to read the file itself)
function readImageSize(src: string, timeoutMs: number): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const image = new Image()
    const timeout = setTimeout(() => resolve(null), timeoutMs)
    image.onload = () => {
      clearTimeout(timeout)
      resolve(image.naturalWidth > 0 ? { width: image.naturalWidth, height: image.naturalHeight } : null)
    }
    image.onerror = () => {
      clearTimeout(timeout)
      resolve(null)
    }
    image.src = src
  })
}

//Images sent with the text (a mail or a document often blocks the images that are only links), with a width that fits a page
//An image the server does not allow to read, or that is too slow, stays a link
export async function inlineHelpImages(html: string, timeoutMs: number = 8000): Promise<string> {
  const root = parse(html)
  await Promise.all(
    Array.from(root.querySelectorAll("img")).map(async (image) => {
      const src = image.getAttribute("src") ?? ""
      if (!src.startsWith("http")) return

      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), timeoutMs)
      let finalSrc = src
      try {
        const response = await fetch(src, { mode: "cors", credentials: "omit", signal: controller.signal })
        if (response.ok) finalSrc = await readAsDataUrl(await response.blob())
      } catch {
        /* the link stays */
      } finally {
        clearTimeout(timeout)
      }

      const size = await readImageSize(finalSrc, timeoutMs)
      if (size) {
        const scale = Math.min(1, MAX_IMAGE_WIDTH / size.width)
        const width = Math.round(size.width * scale)
        const height = Math.round(size.height * scale)
        image.setAttribute("width", String(width))
        image.setAttribute("height", String(height))
        image.setAttribute("style", `${image.getAttribute("style") ?? ""}width:${width}px;height:${height}px;`)
      } else {
        //Size unknown : at least not wider than a page
        image.setAttribute("width", String(MAX_IMAGE_WIDTH))
        image.setAttribute("style", `${image.getAttribute("style") ?? ""}width:${MAX_IMAGE_WIDTH}px;height:auto;`)
      }
      image.setAttribute("src", finalSrc)
    })
  )
  return root.innerHTML
}
